import React, { useEffect, useState } from 'react';
import {
  FDAlert, FDBox, FDHeadlineText,
  FDSecondaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { dataAttrs } from 'Helpers/Testing';
import {
  ActionPaper,
} from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedPostSelectionNextUpActionsStepTemplate/Content/ActionsSection/ActionPaper';
import {
  AskMyPlannerButton,
} from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedPostSelectionNextUpActionsStepTemplate/Content/ActionsSection/AskMyPlannerButton';
import useMobileLayout from 'Hooks/useMobileLayout';
import { partition } from 'Helpers/UtilsWithoutLodash';
import pluralize from 'pluralize';
import { PrimaryActionButtonRel } from 'Packages/FacetCore/Experimental/RelationshipComponents/PrimaryActionButtonRel';
import { ClickableTextRel } from 'Packages/FacetCore/Experimental/RelationshipComponents/ClickableTextRel';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { maxWidthForActionsSection } from './consts';
import { TakeMeToAllActionsButton } from './TakeMeToAllActionsButton';
import { Action } from './Action';

const ActionsSection = ({ parentResource, sectionResource }) => {
  const [shouldShowAll, setShouldShowAll] = React.useState(false);
  const { setPageContentCollection } = usePageContentContext();
  const { displayErrorMessage } = useSnackbarContext();
  const [loading, setLoading] = useState(false);

  const isMobile = useMobileLayout();
  const showAll = () => setShouldShowAll(true);

  const topActions = sectionResource.attr('topActions')?.resources()?.list() ?? [];
  const [completedTopActions, incompleteTopActions] = partition(topActions, action => action.isTruthy('completed'));
  // We don't want to include a "Show all actions" button if there is only one action
  const shouldAllowShowAll = !!incompleteTopActions && incompleteTopActions.length > 1;
  const [showCompletedTopActions, setShowCompletedTopActions] = useState(false);

  const handleExit = async (rel) => {
    try {
      setLoading(true);
      const nextStepColl = await rel.link('related').get();
      setPageContentCollection(nextStepColl);
    } catch (err) {
      displayErrorMessage(err, err.errors || 'Failed to continue. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  };

  const futureActions = sectionResource.attr('futureActions')?.resources()?.list() ?? [];

  const alteredScheduledTransferGroups = parentResource.attr('alteredScheduledTransferGroups')?.resources()?.list() ?? [];

  useEffect(() => {
    if (parentResource.isTruthy('showAllActions')) {
      setShouldShowAll(true);
    }
  }, [parentResource]);


  const ExitButton = (
    <FDStack justifyContent={'center'}>
      { parentResource.withRelationship(fr.relationships.exitFromRoadmapDeliverable.name,
        (rel) => (
          <ClickableTextRel
            {...dataAttrs('exit-button')}
            relationship={rel}
            overrideOnClick={() => handleExit(rel)}
            alignSelf={'center'}
          />
        ),
        () => null)}
    </FDStack>
  );


  return (
    <FDStack direction={'column'} alignItems={'normal'} width={'100%'} maxWidth={maxWidthForActionsSection} spacing={6} {...dataAttrs('scenario-actions-section')}>
      {alteredScheduledTransferGroups?.length > 0 && (
        <FDAlert
          status={parentResource.attr('alteredScheduledTransferGroupsInfoStatus')}
          title={parentResource.attr('alteredScheduledTransferGroupsInfoTitle')}
          description={parentResource.attr('alteredScheduledTransferGroupsInfoSubtitle')}
          details={alteredScheduledTransferGroups?.flatMap((group) => (group?.attr('groupDescriptionDetails')?.map((detail) => (detail))))}
          {...dataAttrs('scenario-funding-instructions-info')}
        />
      )}
      {incompleteTopActions.filter((v, i) => {
        return shouldShowAll || i === 0;
      })
        .map((actionResource) => {
          return (
            <Action
              key={actionResource.id()}
              actionResource={actionResource}
            />
          );
        })}
      {parentResource.withRelationship(fr.relationships.continue.name,
        (rel) => (
          <FDStack justifyContent={'center'}>
            <PrimaryActionButtonRel alignSelf={'center'} relationship={rel} overrideOnClick={() => handleExit(rel)} loading={loading} />
          </FDStack>
        ),
        () => null)}
      {futureActions.length > 0 && (
        <FDStack direction={'column'} alignItems={'center'} width={'100%'} spacing={2}>
          <FDHeadlineText size="md" value={sectionResource.attr('futureActionTitle')} />
          <FDBox width={'100%'}>
            <FDAlert status="info" title={sectionResource.attr('futureActionDescription')} />
          </FDBox>
        </FDStack>
      )}
      {futureActions
        .map((actionResource) => {
          return (
            <Action
              key={actionResource.id()}
              actionResource={actionResource}
            />
          );
        })}
      {completedTopActions.length === 0 ? ExitButton
        : (
          <FDStack direction="column" spacing={8} justifyContent="normal" alignItems="center">
            <FDStack justifyContent="center" direction={'column'} spacing={2}>{/* Avoids stretching this one out */}
              <FDSecondaryActionButton
                label={
              showCompletedTopActions
                ? `Hide completed ${pluralize('action', completedTopActions.length)}`
                : `Show ${completedTopActions.length} completed ${pluralize('action', completedTopActions.length)}`
            }
                loading={loading}
                onClick={() => setShowCompletedTopActions(val => !val)}
              />
              {ExitButton}
            </FDStack>
            {showCompletedTopActions ? (
              <FDStack direction={'column'} spacing={6} width="100%">
                {completedTopActions.map((actionResource) => (
                  <Action
                    key={actionResource.id()}
                    actionResource={actionResource}
                    completed
                  />
                ))}
              </FDStack>
            ) : null}
          </FDStack>
        )}
      {parentResource.withRelationship(
        fr.relationships.guidedHouseholdReviewLayout.name,
        () => null,
        () => (
          <ActionPaper {...dataAttrs('action-section-additional-actions')}>
            <FDStack direction={isMobile ? 'column' : 'row'} justifyContent={'space-between'} spacing={2}>
              {shouldAllowShowAll && !shouldShowAll ? (<FDSecondaryActionButton
                icon={'add'}
                label={'See other actions'}
                isFullWidth
                onClick={showAll}
                {...dataAttrs('see-other-actions-button')}
              />) : null}
              {!shouldAllowShowAll || shouldShowAll ? <TakeMeToAllActionsButton /> : null}
              {parentResource.withRelationship(fr.relationships.scenarioQuestionForPlanner.name, (rel) => {
                return (<AskMyPlannerButton relationship={rel} />);
              }, () => null)}
            </FDStack>
          </ActionPaper>
        ),
      )}
    </FDStack>
  );
};

ActionsSection.defaultProps = {};

ActionsSection.propTypes = {
  parentResource: PropTypes.instanceOf(fr.Resource).isRequired,
  sectionResource: PropTypes.instanceOf(fr.Resource).isRequired,
};

export { ActionsSection };
